import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useMemo, useState} from "react";
import {getSlaReportData, removeSlaReports, getSlaList} from "../../../../../api/evaluation/SlaService";
import {SlaReportChildren} from "./SlaReportChildren";
import {SlaReportItem} from "./SlaReportItem";
import {selectCodesByParentId} from "../../../../../api/code/CodeService";
import {
    DetailHeroSummaryProvider,
    DetailListPage,
    DetailPanel,
    DetailSummaryButton,
    DetailSummaryListButton,
    DetailSummaryPrintButton,
    DetailTabBar,
    useRegisterDetailHeroSummary,
} from "../../../../../components/detailLayout";
import {
    calculateEvaluationScoreByTime,
    calculateConvertedScoreByTime,
    calculateNumberToDay,
    calculateEvaluationScore,
    calculateConvertedScore
} from "../../compare/Compare";
import SlaReportDetailEvidenceTable from "./SlaReportDetailEvidenceTable";
import {formatSlaReportHeroTitle, formatSlaReportMonthLabel} from "../slaReportUtils";
import "../../../../../components/detailLayout/detailListLayout.css";
import "../slaReport.css";

function SlaReportInfo(){
    const { id } = useParams();
    const [sla, setSla] = useState({});
    const [code, setCode] = useState([]);
    const [item, setItem] = useState([]);

    const [detailData, setDetailData] = useState([]);
    const [structure, setStructure] = useState([]);
    const [tabs, setTabs] = useState([
        {clicked: true, name: "월별 보고서"},
        {clicked: false, name: "상세 지표"},
    ]);

    const navigate = useNavigate();
    const cancelUrl = "/evaluation/slaCompare/report";

    // -------------------------------------------------------------------------------------
    // [Print Style 1] 공통: 화면의 불필요한 요소(헤더, 버튼, 탭) 숨김
    // -------------------------------------------------------------------------------------
    const basePrintStyles = `
        @media print {
            header, nav, .page-header-indicator, .page-header-hero, .detail-tab-bar-hero, .detail-list-summary-bar, .no-print {
                display: none !important;
            }
            .common-container {
                margin: 0 !important;
                padding: 0 !important;
                border: none !important;
                box-shadow: none !important;
                width: 100% !important;
                max-width: 100% !important;
            }
        }
    `;

    // -------------------------------------------------------------------------------------
    // [Print Style 2] 상세 탭 전용: A4 가로 보고서 포맷 (강력한 스타일 오버라이드)
    // -------------------------------------------------------------------------------------
    // [Print Style 2] 상세 탭 전용: A4 가로 보고서 포맷
    const detailTabPrintStyles = `
    @media print {
        /* A4 가로 페이지 설정 */
        @page {
            size: A4 landscape;
            margin: 8mm 8mm;
        }

        /* 빈 페이지 방지 */
        html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
        }

        /* 전체 폰트 조정 */
        body {
            font-family: "Malgun Gothic", "Dotum", sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white;
            line-height: 1.2;
        }

        /* 컨테이너 여백 제거 */
        .common-container {
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
        }

        /* 섹션 여백 조정 */
        .section-container {
            margin: 0 !important;
            padding: 0 !important;
        }

        /* 섹션 제목 - 테이블과 함께 유지 */
        h2, h3 {
            font-size: 11pt !important;
            margin: 0 0 5px 0 !important;
            padding: 0 !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            page-break-inside: avoid !important;
            font-weight: bold;
        }

        /* Section 컴포넌트 */
        section {
            margin: 0 !important;
            padding: 0 !important;
            page-break-before: avoid !important;
            page-break-inside: auto !important;
        }

        /* ★ 핵심: 테이블 강제로 첫 페이지부터 시작 */
        table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
            font-size: 9pt !important;
            margin: 0 !important;
            padding: 0 !important;
            page-break-before: avoid !important;
            page-break-inside: auto !important;
        }

        /* ★ 테이블을 포함하는 모든 부모 요소도 페이지 분할 방지 */
        .overflow-box table:first-child {
            page-break-before: avoid !important;
        }

        /* 테이블 헤더 */
        thead {
            display: table-header-group !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
        }

        /* ★ thead의 첫 행 - 무조건 페이지 시작 */
        thead tr:first-child {
            page-break-before: avoid !important;
        }

        /* tbody - 분할 가능 */
        tbody {
            page-break-inside: auto !important;
        }

        /* ★ tbody의 첫 행 - 헤더와 함께 유지 */
        tbody tr:first-child {
            page-break-before: avoid !important;
        }

        /* 셀 기본 스타일 */
        th, td {
            border: 1px solid #000 !important;
            padding: 5px 6px !important;
            word-break: keep-all !important;
            vertical-align: middle !important;
            line-height: 1.3 !important;
        }

        /* 헤더 셀 */
        th {
            background-color: #f0f0f0 !important;
            font-weight: bold !important;
            text-align: center !important;
            white-space: normal !important;
        }

        /* 데이터 셀 */
        td {
            text-align: center !important;
            white-space: normal !important;
        }

        /* 가동률 섹션 각 항목 - 5줄 단위로 분할 방지 */
        tbody tr.maintenance-section-group {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
        }

        /* 장애관리 섹션 - 자동 배치 */
        tbody tr.error-management-start {
            page-break-before: auto !important;
            break-before: auto !important;
        }

        /* 관리 서비스 섹션 - 항상 새 페이지 시작 */
        tbody tr.management-service-start {
            page-break-before: always !important;
            break-before: always !important;
        }

        /* 관리 서비스 전체를 한 페이지에 유지 */
        tbody tr.management-service-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
        }

        /* 관리 서비스 내부 행들 */
        tbody tr.management-service-start ~ tr th,
        tbody tr.management-service-start ~ tr td,
        tbody tr.management-service-start th,
        tbody tr.management-service-start td {
            font-size: 8.5pt !important;
            padding: 3px 5px !important;
            line-height: 1.15 !important;
        }

        /* 스크롤 박스 해제 */
        .overflow-box {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
        }

        /* 합계 행 강조 */
        tbody tr:last-child th,
        tbody tr:last-child td {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
            border-top: 1.5pt solid #000 !important;
            font-size: 9pt !important;
            padding: 5px 6px !important;
        }

        /* 불필요한 요소 제거 */
        button, input, select, textarea, .no-print {
            display: none !important;
        }

        /* 숨겨진 요소의 공간도 완전히 제거 */
        header, nav, .page-header-indicator, .page-header-hero, .detail-tab-bar-hero, .detail-list-summary-bar, .tab-menu-area {
            display: none !important;
            height: 0 !important;
            min-height: 0 !important;
            max-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            line-height: 0 !important;
        }

        /* 여백 최소화 */
        * {
            box-shadow: none !important;
        }

        /* 모든 div의 상단 여백 제거 */
        div {
            page-break-before: avoid !important;
        }

        /* 첫 번째 요소 여백 제거 */
        *:first-child {
            margin-top: 0 !important;
            padding-top: 0 !important;
        }

        /* overflow-box가 페이지 시작 지점에 바로 오도록 */
        .overflow-box:first-child,
        section:first-child {
            page-break-before: avoid !important;
        }
    }
`;








    // ★ 핵심: 현재 탭에 따라 적용할 스타일 결정
    // report 탭일 때는 basePrintStyles만 적용 (기존 화면 유지)
    // detail 탭일 때는 base + detail 스타일 모두 적용 (보고서 포맷 적용)
    const activeTab = tabs.find((tab) => tab.clicked)?.name === "상세 지표" ? "detail" : "report";
    const currentPrintStyles = activeTab === "detail"
        ? basePrintStyles + detailTabPrintStyles
        : basePrintStyles;

    useEffect(() => {
        setTabs([
            {clicked: true, name: "월별 보고서"},
            {clicked: false, name: "상세 지표"},
        ]);
        setItem([]);
        setCode([]);
        setDetailData([]);

        getSlaReportData(id).then((response) => {
            if(response.status === 201) {
                if (response.data.errorCode = 'AUTHORITY') {
                    alert(response.data.reason)
                    navigate(-1)
                }
            }

            if(response.status === 200) {
                const reportData = response.data;
                setSla(reportData);

                const list = reportData.item;
                if(list){
                    const tempItem = [];
                    const tempCode = [];
                    list.forEach((each) => {
                        if (each.section === "item") {
                            tempItem.push(each);
                        } else {
                            tempCode.push(each);
                        }
                    });
                    setItem(tempItem);
                    setCode(tempCode);
                }

                if(reportData.month) {
                    fetchDetailData(reportData.month);
                }

            }
        }).catch(err => {
            console.error("보고서 조회 실패:", err);
        });

        selectCodesByParentId(16).then((res) => setStructure(res));
    }, [id]);

    const fetchDetailData = async (targetMonth) => {
        try {
            if (!targetMonth) return;

            const parts = targetMonth.split("-");
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const lastDay = new Date(year, month, 0).getDate();
            const formattedLastDay = lastDay < 10 ? `0${lastDay}` : lastDay;

            const startDate = `${targetMonth}-01T00:00:00`;
            const endDate = `${targetMonth}-${formattedLastDay}T23:59:59`;

            const searchCondition = {
                searchTxt: "",
                startDate: startDate,
                endDate: endDate
            };

            const pageInfo = {
                page: 0,
                size: 100,
                sort: "createdAt",
                desc: true
            };

            const response = await getSlaList(searchCondition, pageInfo);

            let rawData = response.data ? response.data : response;
            if (rawData.content && Array.isArray(rawData.content)) rawData = rawData.content;
            else if (rawData.list && Array.isArray(rawData.list)) rawData = rawData.list;

            if (!Array.isArray(rawData)) rawData = [];

            if(rawData.length > 0) {
                const filteredData = rawData.filter(d => d.month === targetMonth);
                const finalData = filteredData.length > 0 ? filteredData : rawData;

                const processedData = finalData.map((each) => {
                    const newEach = { ...each };

                    if (newEach.children && newEach.children.length > 0) {
                        newEach.children = newEach.children.map((child) => {
                            const evalScore = calculateEvaluationScoreByTime(child);
                            return {
                                ...child,
                                evaluationScore : evalScore,
                                convertedScore : calculateConvertedScoreByTime(evalScore, child.weight),
                                operationTimeKr : calculateNumberToDay(child.operationTimeToInt),
                                stopTimeKr : calculateNumberToDay(child.stopTimeToInt)
                            };
                        });
                    }
                    if (newEach.item && newEach.item.length > 0) {
                        newEach.item = newEach.item.map((itm) => {
                            const evalScore = calculateEvaluationScore(itm);
                            return {
                                ...itm,
                                evaluationScore : evalScore,
                                convertedScore : calculateConvertedScore(evalScore, itm)
                            };
                        });
                    }
                    return newEach;
                });

                setDetailData(processedData);
            } else {
                setDetailData([]);
            }
        } catch (error) {
            console.error("상세 데이터 로딩 중 에러:", error);
        }
    }

    const remove = async () => {
        if (!window.confirm("삭제 하시겠습니까?")) {
            return;
        }

        const response = await removeSlaReports({ids: [id]});
        if (response.status === 200) {
            alert("삭제되었습니다.");
            navigate(cancelUrl);
        }
    };

    const monthLabel = useMemo(() => formatSlaReportMonthLabel(sla.month), [sla.month]);

    if (!sla || Object.keys(sla).length === 0) {
        return <div className="common-container p-4">데이터를 불러오는 중입니다...</div>;
    }

    return (
        <>
            <style>{currentPrintStyles}</style>
            <div className="common-container container-height detail-list-layout asset-detail-list-layout border-l-gray sla-report-print">
                <DetailHeroSummaryProvider>
                    <SlaReportView
                        id={id}
                        sla={sla}
                        code={code}
                        item={item}
                        detailData={detailData}
                        structure={structure}
                        monthLabel={monthLabel}
                        tabs={tabs}
                        setTabs={setTabs}
                        onRemove={remove}
                        cancelUrl={cancelUrl}
                    />
                </DetailHeroSummaryProvider>
            </div>
        </>
    );
}

function SlaReportView({
    id,
    sla,
    code,
    item,
    detailData,
    structure,
    monthLabel,
    tabs,
    setTabs,
    onRemove,
    cancelUrl,
}) {
    const navigate = useNavigate();
    const heroTitle = useMemo(() => formatSlaReportHeroTitle(sla), [sla]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.altKey && event.key === "l") {
                if (window.confirm("목록으로 돌아가시겠습니까?")) {
                    navigate(cancelUrl);
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [cancelUrl, navigate]);

    const summaryActions = (
        <>
            <DetailSummaryPrintButton/>
            {sla.auth?.update ? (
                <DetailSummaryButton onClick={() => navigate(`/evaluation/slaCompare/report/update/${id}`)}>
                    수정
                </DetailSummaryButton>
            ) : null}
            {sla.auth?.update ? (
                <DetailSummaryButton danger onClick={onRemove}>
                    삭제
                </DetailSummaryButton>
            ) : null}
            <DetailSummaryListButton url={cancelUrl}>목록</DetailSummaryListButton>
        </>
    );

    useRegisterDetailHeroSummary({
        title: heroTitle,
        meta: "평가관리/보고서 > SLA 관리 > SLA 월별보고서 > 상세",
        actions: summaryActions,
    });

    const reportTabActive = tabs[0]?.clicked;
    const detailTabActive = tabs[1]?.clicked;

    return (
        <>
            <DetailTabBar tabs={tabs} setTabs={setTabs} className="no-print"/>

            {reportTabActive && (
                <DetailListPage className="flex min-h-0 flex-1 flex-col">
                    <div className="container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="asset-detail-list-scroll detail-list-scroll">
                            <DetailPanel title="서비스 수준협약(SLA) 평가표">
                            <table className="w-full create-table" style={{tableLayout: "fixed"}}>
                                <thead>
                                <tr>
                                    <th className="fieldset-th-sla pt-1.5 pb-1.5">종합평가점수</th>
                                    <th className="fieldset-th-sla pt-1.5 pb-1.5">95점 이상</th>
                                    <th className="fieldset-th-sla pt-1.5 pb-1.5">85점 이상 ~ 95점 미만</th>
                                    <th className="fieldset-th-sla pt-1.5 pb-1.5">75점 이상 ~ 85점 미만</th>
                                    <th className="fieldset-th-sla pt-1.5 pb-1.5">65점 이상 ~ 75점 미만</th>
                                    <th className="fieldset-th-sla pt-1.5 pb-1.5">65점 미만</th>
                                </tr>
                                <tr>
                                    <th className="fieldset-th-sla">평가등급</th>
                                    <td className="fieldset-td-sla pt-1.5 pb-1.5" style={sla.evaluationResult >= 95 ? {background: "rgb(215, 75, 75)", color: "rgb(255, 255, 255)"} : {}}>A(탁월)</td>
                                    <td className="fieldset-td-sla pt-1.5 pb-1.5" style={(sla.evaluationResult >= 85 && sla.evaluationResult < 95) ? {background: "rgb(215, 75, 75)", color: "rgb(255, 255, 255)"} : {}}>B(우수)</td>
                                    <td className="fieldset-td-sla pt-1.5 pb-1.5" style={(sla.evaluationResult >= 75 && sla.evaluationResult < 85) ? {background: "rgb(215, 75, 75)", color: "rgb(255, 255, 255)"} : {}}>C(보통)</td>
                                    <td className="fieldset-td-sla pt-1.5 pb-1.5" style={(sla.evaluationResult >= 65 && sla.evaluationResult < 75) ? {background: "rgb(215, 75, 75)", color: "rgb(255, 255, 255)"} : {}}>D(미흡)</td>
                                    <td className="fieldset-td-sla pt-1.5 pb-1.5" style={sla.evaluationResult < 65 ? {background: "rgb(215, 75, 75)", color: "rgb(255, 255, 255)"} : {}}>E(불량)</td>
                                </tr>
                                </thead>
                            </table>
                            </DetailPanel>

                            <DetailPanel title="항목별 평가(월간)">
                                <div className="overflow-x-auto">
                                    <table className="w-full create-table table-border" style={{tableLayout: "fixed"}}>
                                        <thead>
                                        <tr>
                                            <th rowSpan={2} className="fieldset-th-sla w-[100px]">서비스 구분</th>
                                            <th rowSpan={2} colSpan={2} className="fieldset-th-sla w-[350px]">측정지표</th>
                                            <th rowSpan={2} className="fieldset-th-sla">가중치</th>
                                            <th rowSpan={2} className="fieldset-th-sla">측정주기</th>
                                            <th colSpan={2} className="fieldset-th-sla">서비스 목표수준</th>
                                            <th colSpan={2} className="fieldset-th-sla">{monthLabel}월 실적(수행사)</th>
                                            <th colSpan={2} className="fieldset-th-sla">{monthLabel}월 실적(고객사)</th>
                                            <th rowSpan={2} className="fieldset-th-sla">비고</th>
                                        </tr>
                                        <tr>
                                            <th className="fieldset-th-sla">목표 수준</th>
                                            <th className="fieldset-th-sla">최소 수준</th>
                                            <th className="fieldset-th-sla">측정 결과</th>
                                            <th className="fieldset-th-sla">평가 점수</th>
                                            <th className="fieldset-th-sla">측정 결과</th>
                                            <th className="fieldset-th-sla">평가 점수</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {code.length > 0 && <SlaReportChildren data={code} />}
                                        {item.length > 0 && <SlaReportItem data={item} />}
                                        <tr>
                                            <th className="fieldset-th-sla pt-1.5 pb-1.5" colSpan={8}>합계</th>
                                            <td className="fieldset-td-sla">{sla.evaluationResult ? `${sla.evaluationResult}%` : "0%"}</td>
                                            <th className="fieldset-th-sla pt-1.5 pb-1.5"/>
                                            <td className="fieldset-td-sla">{sla.customerEvaluationResult ? `${sla.customerEvaluationResult}%` : "0%"}</td>
                                            <th className="fieldset-th-sla pt-1.5 pb-1.5"/>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </DetailPanel>
                        </div>
                    </div>
                </DetailListPage>
            )}

            {detailTabActive && (
                <DetailListPage className="flex min-h-0 flex-1 flex-col">
                    <div className="container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="asset-detail-list-scroll detail-list-scroll">
                            <DetailPanel>
                                {detailData.length === 0 ? (
                                    <div className="p-10 text-center text-gray-500 bg-gray-50 rounded">
                                        <p>상세 데이터를 조회할 수 없습니다.</p>
                                    </div>
                                ) : (
                                    <div className="overflow-box overflow-x-auto">
                                        <SlaReportDetailEvidenceTable
                                            data={detailData}
                                            structure={structure}
                                            month={sla.month}
                                        />
                                    </div>
                                )}
                            </DetailPanel>
                        </div>
                    </div>
                </DetailListPage>
            )}
        </>
    );
}

export {SlaReportInfo};